// Data Export Service - Handles exporting and restoring user data
import api, { apiClient } from './api';
import { getLocalDateString } from '../utils/timezones';

export interface UserDataExport {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    bio: string | null;
    photoUrl: string | null;
    createdAt: string;
    updatedAt: string;
  };
  settings: {
    theme: string;
    notifications: boolean;
    language: string;
    timezone: string;
  } | null;
  journalEntries: any[];
  visionItems: any[];
  routines: any[];
  calendarEvents: any[];
  tasks: any[];
  contributions: any[];
  healthEntries: any[];
  checklists: any[];
  budgetData: {
    categories: any[];
    transactions: any[];
    categoryBudgets: any[];
    reserves: any[];
    savingsGoals: any[];
    notifications: any[];
    reports: any[];
  };
  metadata: {
    exportDate: string;
    exportVersion: string;
    totalRecords: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

class DataExportService {
  /**
   * Export all user data as JSON
   */
  async exportData(): Promise<UserDataExport> {
    const response = await api.get<ApiResponse<UserDataExport>>('/data/export');
    return response.data.data;
  }

  /**
   * Export all user data as CSV and download
   */
  async exportDataCSV(): Promise<void> {
    try {
      // Use the axios client directly for blob response
      const client = apiClient.getClient();
      const response = await client.get('/data/export/csv', {
        responseType: 'blob',
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const date = getLocalDateString();
      link.download = `infinity-rockets-export-${date}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      throw error;
    }
  }

  /**
   * Export all user data as JSON and download
   */
  async exportDataJSON(): Promise<void> {
    try {
      const data = await this.exportData();
      
      // Convert to JSON string with pretty formatting
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const date = getLocalDateString();
      link.download = `infinity-rockets-export-${date}.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting JSON:', error);
      throw error;
    }
  }

  /**
   * Restore data from backup
   */
  async restoreData(backupData: UserDataExport): Promise<void> {
    const response = await api.post<ApiResponse<{ message: string }>>('/data/restore', {
      data: backupData,
    });
    return;
  }

  /**
   * Load backup file from user's computer
   */
  async loadBackupFile(file: File): Promise<UserDataExport> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const data = JSON.parse(content) as UserDataExport;
          
          // Validate backup structure
          if (!data.user || !data.metadata) {
            throw new Error('Invalid backup file format');
          }
          
          resolve(data);
        } catch (error) {
          reject(new Error('Failed to parse backup file. Please ensure it is a valid JSON file.'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read backup file'));
      };
      
      reader.readAsText(file);
    });
  }
}

export const dataExportService = new DataExportService();
export default dataExportService;

